import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, cents, day, month, points, useCurrentCompany } from '../../lib'
import { Empty, ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import { startBankSetup } from './bankSetup'
import type { Redemption, RedemptionStatus } from './types'

export const STATUS_TEXT: Record<RedemptionStatus, string> = {
  needs_bank: 'Add your bank',
  pending_approval: 'Pending approval',
  held: 'On hold',
  approved: 'Approved',
  approved_manual: 'Approved; payment on its way',
  submitted: 'Payment sent',
  paid: 'Paid',
  rejected: 'Rejected',
  canceled: 'Canceled',
  failed: 'Payment failed; points back',
  returned: 'Payment bounced; points back',
  unknown: 'Checking payment',
  requested: 'Waiting for the partner to apply the credit',
  confirmed: 'Credit applied',
  refused: 'Partner refused; points back',
  expired: 'Expired; points back',
}

const CANCELABLE: RedemptionStatus[] = ['needs_bank', 'pending_approval', 'held']

export function RedemptionsPage() {
  const company = useCurrentCompany()
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['redeem', 'list', company?.id],
    queryFn: () => api<{ rows: Redemption[] }>('GET', `/api/companies/${company!.id}/redemptions`),
    enabled: !!company,
    refetchInterval: 5000,
  })
  const cancel = useMutation({
    mutationFn: (id: string) => api<Redemption>('POST', `/api/redemptions/${id}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['redeem'] }),
  })
  const bank = useMutation({
    mutationFn: () => api<{ url: string }>('POST', `/api/companies/${company!.id}/payout-recipient/link`),
    onSuccess: (r) => {
      startBankSetup(company!.id)
      window.location.assign(r.url)
    },
  })

  if (!company) return <NoCompany />
  const rows = list.data?.rows ?? []
  const anyCredit = rows.some((r) => r.method !== 'cash')     // cash only in v1: no column that says so
  return (
    <div className="page stack">
      <PageBand title="Payouts" action={company.role === 'admin'
        ? <Link className="e-btn e-btn-primary" to="/redeem" data-testid="redeem-new">Redemption settings</Link> : undefined} />
      {list.isLoading && <Loading />}
      {list.error && <ErrorBox error={list.error} />}
      {cancel.error && <ErrorBox error={cancel.error} />}
      {!list.isLoading && rows.length === 0 && <Empty>No payouts yet. We pay once a month.</Empty>}
      {rows.length > 0 && (
        <div className="e-panel table-wrap">
          <table>
            <thead><tr><th>Month</th><th>Partner</th>{anyCredit && <th>Kind</th>}<th>Points</th><th>Amount</th><th>Status</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="redeem-row" data-id={r.id}>
                  <td>{r.month ? month(r.month) : day(r.created_at)}</td>
                  <td>{r.partner_name}</td>
                  {anyCredit && <td>{r.method === 'cash' ? 'Cash rebate' : 'Bill credit'}</td>}
                  <td className="num">{points(r.points)}</td>
                  <td className="num">{cents(r.amount_cents)}</td>
                  <td data-testid="redeem-status" data-status={r.status}>{STATUS_TEXT[r.status] ?? r.status}</td>
                  <td>
                    {company.role === 'admin' && r.status === 'needs_bank' && (
                      <button className="button" data-testid="redeem-add-bank" onClick={() => bank.mutate()}>Add bank</button>
                    )}
                    {company.role === 'admin' && CANCELABLE.includes(r.status) && (
                      <button className="link-button" data-testid="redeem-cancel" onClick={() => cancel.mutate(r.id)}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
